import { control } from "./keys.js";

/*
 * The key row of an SSH terminal on phones and tablets (docs/terminal.md,
 * "Touch keys"): the keys a system keyboard lacks, docked right above it,
 * and a button that puts the system keyboard away and brings it back.
 * What each key sends and when the row is on screen; the component is
 * `touch-keys-row.tsx`.
 */

const ESC = "\x1b";

/** A key of the row; `keyboard` (show or hide the system keyboard) is the row's own button. */
export type TouchKey = "esc" | "tab" | "ctrl" | "alt" | "left" | "up" | "down" | "right" | "slash";

/** A key held for the next one: tapped once it latches, the next key goes with it. */
export type StickyModifier = "ctrl" | "alt";

/**
 * The row, in its order (owner's request of 2026-10-22, after Termius):
 * «esc · tab · ctrl · alt · ◀ ▲ ▼ ▶ · /», then the keyboard button at the
 * right end. A second page (`|`, `-`, `~`, `$`, `*`) is a follow-up.
 */
export const TOUCH_KEYS: readonly TouchKey[] = [
  "esc",
  "tab",
  "ctrl",
  "alt",
  "left",
  "up",
  "down",
  "right",
  "slash",
];

/** What a key shows (monospace); its accessible name is in `terminal.touchKeys.<key>`. */
export const TOUCH_KEY_LABEL: Record<TouchKey, string> = {
  esc: "esc",
  tab: "tab",
  ctrl: "ctrl",
  alt: "alt",
  left: "◀",
  up: "▲",
  down: "▼",
  right: "▶",
  slash: "/",
};

export const isSticky = (key: TouchKey): key is StickyModifier => key === "ctrl" || key === "alt";

/** Keys that send one fixed code (with Alt: ESC before it). */
const PLAIN: Partial<Record<TouchKey, string>> = { esc: ESC, tab: "\t", slash: "/" };
/** Ctrl+/ as xterm sends it: `^_` (0x1F). */
const CTRL_SLASH = "\x1f";

/** The final byte of an arrow's sequence. */
const ARROW: Partial<Record<TouchKey, string>> = { up: "A", down: "B", right: "C", left: "D" };

export interface KeyModes {
  /** Ctrl is held (the sticky Ctrl of the row). */
  ctrl: boolean;
  /** Alt is held (the sticky Alt of the row). */
  alt?: boolean;
  /** DECCKM is on (vim, less, htop…): arrows send `ESC O x` instead of `ESC [ x`. */
  applicationCursor: boolean;
}

/**
 * xterm's modifier parameter of a cursor key: 1 + Shift(1) + Alt(2) +
 * Ctrl(4); 1 means none.
 */
const modifierParameter = (modes: KeyModes) => 1 + (modes.alt ? 2 : 0) + (modes.ctrl ? 4 : 0);

/**
 * The bytes a key of the row sends, as xterm sends them for a hardware key.
 * The modifiers themselves send nothing (they are held for the next key).
 * An arrow with Ctrl and/or Alt is `ESC [ 1 ; <5|3|7> x` in either cursor
 * mode; Alt before any other key is a leading ESC (xterm's default
 * `metaSendsEscape`); Ctrl+/ is `^_` as xterm sends it, Ctrl+Tab and
 * Ctrl+Esc have no codes of their own and send Tab and Esc.
 */
export function touchKeyBytes(key: TouchKey, modes: KeyModes): string {
  const arrow = ARROW[key];
  if (arrow) {
    const modifier = modifierParameter(modes);
    if (modifier > 1) return `${ESC}[1;${modifier}${arrow}`;
    return modes.applicationCursor ? `${ESC}O${arrow}` : `${ESC}[${arrow}`;
  }
  const plain = key === "slash" && modes.ctrl ? CTRL_SLASH : (PLAIN[key] ?? "");
  if (!plain) return "";
  return modes.alt ? `${ESC}${plain}` : plain;
}

/**
 * What the system keyboard typed while Ctrl was held: one character becomes
 * its control code (Ctrl, then «c» → `^C`); anything else — a character
 * without one, a word from autocorrect, a paste — goes as it is.
 */
export function withCtrl(data: string): string {
  const chars = [...data];
  const first = chars[0];
  if (chars.length !== 1 || first === undefined) return data;
  return control(first) ?? data;
}

/**
 * What the system keyboard typed while the row's modifiers were held: Ctrl
 * as `withCtrl`, Alt as a leading ESC (Alt+b is `ESC b`, "word back" in a
 * shell), both together as ESC and the control code.
 */
export function withModifiers(data: string, held: { ctrl: boolean; alt: boolean }): string {
  if (!data) return data;
  const typed = held.ctrl ? withCtrl(data) : data;
  return held.alt ? `${ESC}${typed}` : typed;
}

/**
 * A phone or tablet: its main pointer is a finger and no mouse or trackpad
 * is attached. Touch-screen laptops and desktops have a fine pointer and a
 * keyboard, and never get the row.
 */
export const isTouchOnly = (matches: (query: string) => boolean): boolean =>
  matches("(pointer: coarse)") && !matches("(any-pointer: fine)");

/**
 * Whether the system keyboard is on screen, as far as the page can tell: the
 * visual viewport is shorter than the layout viewport by more than this.
 * Smaller differences are the browser's own bars coming and going.
 */
export const KEYBOARD_MIN_PX = 120;

/**
 * Where the system keyboard is, as far as the page can tell while a
 * terminal has the focus:
 * - `idle`: the focus came without a tap (after connecting, from a tab) —
 *   iOS opens no keyboard for that, so neither does the row;
 * - `tapped`: the person tapped the terminal, the keyboard is coming (some
 *   browsers never shrink the viewport for it, a test has none) — shown;
 * - `open`: the visual viewport shrank — shown, right above the keyboard;
 * - `closed`: it grew back after `open` — the keyboard was put away while
 *   the terminal kept the focus, and the row goes with it until a new tap.
 */
export type KeyboardGuess = "idle" | "tapped" | "open" | "closed";

export interface ViewportSize {
  /** The layout viewport (`documentElement.clientHeight`), CSS px. */
  layoutHeight: number;
  /** `visualViewport.height`. */
  visualHeight: number;
  /** `visualViewport.scale`: pinch zoom shrinks the visual viewport too. */
  scale: number;
}

export type KeyboardSignal =
  { type: "tap" } | { type: "blur" } | { type: "viewport"; size: ViewportSize };

export const keyboardOpen = (size: ViewportSize): boolean =>
  size.layoutHeight - size.visualHeight * size.scale > KEYBOARD_MIN_PX;

export function nextKeyboardGuess(previous: KeyboardGuess, event: KeyboardSignal): KeyboardGuess {
  if (event.type === "blur") return "idle";
  if (event.type === "tap") return previous === "open" ? "open" : "tapped";
  if (keyboardOpen(event.size)) return "open";
  return previous === "open" ? "closed" : previous;
}

/**
 * The row is on screen on a touch-only device while a terminal has the
 * focus and the keyboard is out or coming — or after the person put the
 * keyboard away with the row's own button (`parked`): then the row stays at
 * the bottom, its keys still work, and the button brings the keyboard back.
 */
export const showTouchKeys = (
  touch: boolean,
  focused: boolean,
  keyboard: KeyboardGuess,
  parked = false,
): boolean => touch && (parked || (focused && (keyboard === "tapped" || keyboard === "open")));
